import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Parcel, Waybill, Dealer } from './shipment-request.service';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';

/**
 * Service for generating waybill documents as PDF
 * Generates professional transport documents based on the provided HTML layout
 * Uses the same interfaces as ShipmentRequestService for consistency
 */
@Injectable({
  providedIn: 'root'
})
export class WaybillDocumentService {
  // Standard A4 page dimensions in mm
  private readonly PAGE_WIDTH = 210;
  private readonly PAGE_HEIGHT = 297;
  private readonly MARGIN = 10;

  constructor() { }

  /**
   * Generate a complete waybill document PDF
   * @param waybill The waybill data
   * @param parcels Array of parcels for the waybill
   * @param dealer Dealer information for the waybill
   * @returns Observable with success status
   */
  generateWaybillDocument(waybill: Waybill, parcels: Parcel[], dealer?: Dealer): Observable<boolean> {
    const generateAsync = async () => {
      try {
        // Create PDF document with A4 size
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // Draw the waybill document
        await this.drawWaybillDocument(pdf, waybill, parcels, dealer);

        // Download the PDF
        const filename = `Bolla_${waybill.waybillCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
        pdf.save(filename);

        return true;
      } catch (error) {
        console.error('Error generating waybill document:', error);
        return false;
      }
    };

    return new Observable(observer => {
      generateAsync().then(result => {
        observer.next(result);
        observer.complete();
      }).catch(error => {
        console.error('Error in async waybill document generation:', error);
        observer.next(false);
        observer.complete();
      });
    });
  }

  /**
   * Draw the complete waybill document on PDF
   * @param pdf jsPDF instance
   * @param waybill Waybill data
   * @param parcels Array of parcels
   * @param dealer Dealer information
   */
  private async drawWaybillDocument(pdf: jsPDF, waybill: Waybill, parcels: Parcel[], dealer?: Dealer): Promise<void> {
    // Set default font
    pdf.setFont('helvetica');

    // Draw header
    this.drawHeader(pdf, waybill);

    // Draw main information section
    let currentY = this.drawMainInfo(pdf, waybill, dealer);

    // Draw parcels table
    currentY = this.drawParcelsTable(pdf, parcels, currentY);

    // Draw summary
    currentY = this.drawSummary(pdf, parcels, currentY);

    // Draw notes and signatures section
    currentY = this.drawNotesAndSignatures(pdf, waybill, currentY);

    // Draw barcode section
    await this.drawBarcodeSection(pdf, waybill, currentY);

    // Draw footer
    this.drawFooter(pdf);
  }
  /**
   * Draw the header section with company information
   */
  private drawHeader(pdf: jsPDF, waybill: Waybill): void {
    const startY = this.MARGIN + 5;
    
    // BMW Italia section (left)
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BMW ITALIA S.p.A', this.MARGIN, startY);
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('[Committente]', this.MARGIN, startY + 5);
    
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    const bmwInfo = [
      'Sede Legale: Via della Unione Europea, 1 - 20097 San Donato Milanese (MI)',
      'Capitale Sociale: 5.300.000 di Euro i.v.',
      'R.E.A. MI 1403223 - Reg. Impr. 01934110154',
      'Codice Fiscale 01934110154 - Partita IVA 12532500159',
      'Società unipersonale soggetta a direzione e coord. di BMW AG.',
      'Unità Locale: Via Passo di Napoleone, 91',
      '37020 Dolcè (VR) - Tel.: 02 51610111 - Fax: 02 51610222',
      'Registro Pile e Accumulatori: IT09060P00000647',
      'Registro Nazionale Produttori: AEE IT12100000007730'
    ];
    
    bmwInfo.forEach((line, index) => {
      pdf.text(line, this.MARGIN, startY + 10 + (index * 3));
    });

    // Bertani Trasporti section (right)
    const rightColumnX = this.PAGE_WIDTH / 2 + 5;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BERTANI TRASPORTI S.p.A', rightColumnX, startY);
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('[Vettore Trasportatore]', rightColumnX, startY + 5);
    
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    const bertaniInfo = [
      'Sede Legale: Via Europa, 26 - 46043 Castiglione delle Stiviere (MN)',
      'Capitale Sociale: 12.000.000 di Euro i.v.',
      'R.E.A. MN 125922 - Reg. Impr.MN 00247680200',
      'Codice Fiscale 00247680200 - Partita IT 00247680200',
      'DUNS Number 43-071-9179',
      'Ufficio: Via Cavour, 58 - 46043 Castiglione delle Stiviere (MN)',
      'Tel.: +39 0376 6781 - Fax: +39 0376 671177',
      'PEC: bertani@pec.bertanitrasporti.it'
    ];
    
    bertaniInfo.forEach((line, index) => {
      pdf.text(line, rightColumnX, startY + 10 + (index * 3));
    });
  }
  /**
   * Draw main shipment information section
   */
  private drawMainInfo(pdf: jsPDF, waybill: Waybill, dealer?: Dealer): number {
    const startY = 50;
    const contentWidth = this.PAGE_WIDTH - (2 * this.MARGIN);
    const halfWidth = contentWidth / 2;
    
    // Draw border
    pdf.setLineWidth(0.5);
    pdf.rect(this.MARGIN, startY, contentWidth, 30);
    
    // Shipment number and date
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    const formattedDate = waybill.waybillDate ? 
      new Date(waybill.waybillDate).toLocaleDateString('it-IT') : 
      new Date().toLocaleDateString('it-IT');
    
    pdf.text(`Spedizione N°. ${waybill.waybillCode || ''} Del ${formattedDate}`, this.MARGIN + 2, startY + 5);
    
    // Vertical line separator
    pdf.line(this.MARGIN + halfWidth, startY, this.MARGIN + halfWidth, startY + 15);
    
    // Destinatario section
    pdf.text(`Destinatario: ${dealer?.dealerCode || ''}`, this.MARGIN + 2, startY + 10);
    pdf.text(`Destinazione merce / Luogo di scarico: ${dealer?.dealerCode || ''}`, this.MARGIN + halfWidth + 2, startY + 10);
    
    // Horizontal line
    pdf.line(this.MARGIN, startY + 15, this.MARGIN + contentWidth, startY + 15);
    
    // Dealer information (left side)
    if (dealer) {
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(dealer.dealerName || '', this.MARGIN + 2, startY + 20);
      
      pdf.setFont('helvetica', 'normal');
      const address = `${dealer.dealerOperatingAddressStreet || ''} ${dealer.dealerOperatingAddressNumber || ''}`;
      pdf.text(address, this.MARGIN + 2, startY + 24);
      
      const cityInfo = `${dealer.dealerOperatingAddressZip || ''}, ${dealer.dealerOperatingAddressCity || ''} (${dealer.dealerOperatingAddressProvince || ''})`;
      pdf.text(cityInfo, this.MARGIN + 2, startY + 28);
        const vatPhone = `P.Iva ${dealer.dealerVatNumber || ''} Tel. -`;
      pdf.text(vatPhone, this.MARGIN + 2, startY + 32);
    }
    
    // Same information for destination (right side) - in real case this might be different
    if (dealer) {
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(dealer.dealerName || '', this.MARGIN + halfWidth + 2, startY + 20);
      
      pdf.setFont('helvetica', 'normal');
      const address = `${dealer.dealerOperatingAddressStreet || ''} ${dealer.dealerOperatingAddressNumber || ''}`;
      pdf.text(address, this.MARGIN + halfWidth + 2, startY + 24);
      
      const cityInfo = `${dealer.dealerOperatingAddressZip || ''}, ${dealer.dealerOperatingAddressCity || ''} (${dealer.dealerOperatingAddressProvince || ''})`;
      pdf.text(cityInfo, this.MARGIN + halfWidth + 2, startY + 28);
      
      const vatPhone = `P.Iva ${dealer.dealerVatNumber || ''} Tel. -`;
      pdf.text(vatPhone, this.MARGIN + halfWidth + 2, startY + 32);
    }
    
    // Additional info
    pdf.line(this.MARGIN, startY + 35, this.MARGIN + contentWidth, startY + 35);
    pdf.text(`Tipo Consegna: ${waybill.waybillDeliveryType || 'STANDARD'}`, this.MARGIN + 2, startY + 40);
    
    pdf.line(this.MARGIN, startY + 42, this.MARGIN + contentWidth, startY + 42);
    pdf.text(`TP ultimo Miglio: ${waybill.waybillCarrierCode || ''}`, this.MARGIN + 2, startY + 47);
    
    return startY + 52;
  }
  /**
   * Draw parcels table
   */
  private drawParcelsTable(pdf: jsPDF, parcels: Parcel[], startY: number): number {
    const tableStartY = startY + 5;
    let currentY = tableStartY;
    const contentWidth = this.PAGE_WIDTH - (2 * this.MARGIN);
    
    // Table headers
    const headers = ['N° Imballo', 'Descrizione', 'Tipo', 'Peso (kg)', 'Volume (m³)', 'Danno', 'Mancanza'];
    const columnWidths = [25, 60, 25, 20, 25, 20, 20];
    let currentX = this.MARGIN;
    
    // Draw header row
    pdf.setFillColor(238, 238, 238);
    pdf.rect(this.MARGIN, currentY, contentWidth, 6, 'F');
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    
    headers.forEach((header, index) => {
      pdf.text(header, currentX + 1, currentY + 4);
      currentX += columnWidths[index];
    });
    
    currentY += 6;
    
    // Draw parcels
    pdf.setFont('helvetica', 'normal');
    parcels.forEach((parcel, index) => {
      currentX = this.MARGIN;
      
      // Check if we need a new page
      if (currentY > 250) {
        pdf.addPage();
        currentY = 20;
      }
      
      const rowData = [
        parcel.parcelCode || '',
        this.truncateText(parcel.parcelDescription || '', 40),
        parcel.parcelType || '',
        (parcel.parcelWeight || 0).toString(),
        this.formatVolume(parcel.parcelVolume || 0),
        '', // Danno - empty circle
        ''  // Mancanza - empty circle
      ];
      
      // Draw row border
      pdf.setLineWidth(0.3);
      pdf.rect(this.MARGIN, currentY, contentWidth, 6);
      
      rowData.forEach((data, colIndex) => {
        if (colIndex < 5) { // Text columns
          pdf.text(data, currentX + 1, currentY + 4);        } else { // Circle columns for Danno/Mancanza
          pdf.setLineWidth(0.5);
          pdf.circle(currentX + 10, currentY + 3, 2, 'S');
        }
        currentX += columnWidths[colIndex];
      });
      
      currentY += 6;
    });
    
    return currentY + 5;
  }
  /**
   * Draw summary section
   */
  private drawSummary(pdf: jsPDF, parcels: Parcel[], startY: number): number {
    const totalParcels = parcels.length;
    const totalWeight = parcels.reduce((sum, p) => sum + (p.parcelWeight || 0), 0);
    const totalVolume = parcels.reduce((sum, p) => sum + (p.parcelVolume || 0), 0);
    const contentWidth = this.PAGE_WIDTH - (2 * this.MARGIN);
    const colWidth = contentWidth / 3;
    
    // Draw summary table
    pdf.setLineWidth(0.5);
    pdf.rect(this.MARGIN, startY, contentWidth, 12);
    
    // Headers
    pdf.setFillColor(238, 238, 238);
    pdf.rect(this.MARGIN, startY, contentWidth, 6, 'F');
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Numero Colli', this.MARGIN + 10, startY + 4);
    pdf.text('Peso Totale (kg)', this.MARGIN + colWidth + 10, startY + 4);
    pdf.text('Volume totale (m³)', this.MARGIN + (colWidth * 2) + 10, startY + 4);
    
    // Vertical lines
    pdf.line(this.MARGIN + colWidth, startY, this.MARGIN + colWidth, startY + 12);
    pdf.line(this.MARGIN + (colWidth * 2), startY, this.MARGIN + (colWidth * 2), startY + 12);
    
    // Values
    pdf.setFontSize(10);
    pdf.text(totalParcels.toString(), this.MARGIN + (colWidth / 2), startY + 10);
    pdf.text(totalWeight.toFixed(2), this.MARGIN + colWidth + (colWidth / 2), startY + 10);
    pdf.text(this.formatVolume(totalVolume), this.MARGIN + (colWidth * 2) + (colWidth / 2), startY + 10);
    
    return startY + 17;
  }
  /**
   * Draw notes and signatures section
   */
  private drawNotesAndSignatures(pdf: jsPDF, waybill: Waybill, startY: number): number {
    const contentWidth = this.PAGE_WIDTH - (2 * this.MARGIN);
    const halfWidth = contentWidth / 2;
    
    // Notes and signatures table
    pdf.setLineWidth(0.5);
    pdf.rect(this.MARGIN, startY, contentWidth, 40);
    
    // Headers
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Note', this.MARGIN + 2, startY + 4);
    pdf.text('Timbro e Firma Vettore', this.MARGIN + halfWidth + 2, startY + 4);
    
    // Vertical line
    pdf.line(this.MARGIN + halfWidth, startY, this.MARGIN + halfWidth, startY + 20);
    pdf.line(this.MARGIN, startY + 5, this.MARGIN + contentWidth, startY + 5);
    
    // Notes content
    if (waybill.waybillNote) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      const noteLines = this.splitTextIntoLines(waybill.waybillNote, 35);
      noteLines.forEach((line, index) => {
        if (index < 3) { // Limit to 3 lines
          pdf.text(line, this.MARGIN + 2, startY + 10 + (index * 3));
        }
      });
    }
    
    // Second row headers
    pdf.line(this.MARGIN, startY + 20, this.MARGIN + contentWidth, startY + 20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Barcode', this.MARGIN + 2, startY + 24);
    pdf.text('Timbro e Firma Destinatario', this.MARGIN + halfWidth + 2, startY + 24);
    
    return startY + 45;
  }
  /**
   * Draw barcode section
   */
  private async drawBarcodeSection(pdf: jsPDF, waybill: Waybill, startY: number): Promise<void> {
    try {
      // Generate barcode
      const barcodeText = waybill.waybillCode || 'DEFAULT';
      const canvas = document.createElement('canvas');
      
      JsBarcode(canvas, barcodeText, {
        format: 'CODE128',
        width: 2,
        height: 40,
        displayValue: true,
        fontSize: 12,
        textMargin: 2
      });
      
      // Convert canvas to image data
      const barcodeDataUrl = canvas.toDataURL('image/png');
      
      // Add barcode to PDF
      pdf.addImage(barcodeDataUrl, 'PNG', this.MARGIN + 10, startY - 35, 50, 15);
      
      // Add tracking code
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Tracking code: ${waybill.waybillTrackingNumber || waybill.waybillCode || ''}`, this.MARGIN + 2, startY + 5);
      
    } catch (error) {
      console.error('Error generating barcode:', error);
      // Fallback: just display the text
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(waybill.waybillCode || 'NO BARCODE', this.MARGIN + 35, startY - 25);
    }
  }
  /**
   * Draw footer
   */
  private drawFooter(pdf: jsPDF): void {
    const footerY = this.PAGE_HEIGHT - 25;
    
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    
    const footerText = [
      'Tipologia merce contenuta nei colli: Ricambi ed Accessoristica',
      'La fattura immediata viene emessa e spedita al cessionario entro le ore 24 del giorno di',
      'effettuazione dell\'operazione. (Circolare Ministeriale 225/E del 15/09/1996)',
      'Indicazione ai sensi dell\'articolo 7 D.Lgs 286/2005: Il trasporto deve essere eseguito a cura',
      'del vettore nel rispetto delle disposizioni vigenti in materia di sicurezza stradale, della',
      'circolazione stradale e della sicurezza sociale con particolare riferimento ai tempi di guida e',
      'di riposo del conducente.'
    ];
    
    footerText.forEach((line, index) => {
      pdf.text(line, this.MARGIN, footerY + (index * 3));
    });
    
    // Copy designation
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('COPIA PER VETTORE', this.PAGE_WIDTH / 2 - 20, footerY + 22);
  }

  /**
   * Helper method to truncate text
   */
  private truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Helper method to format volume
   */
  private formatVolume(volume: number): string {
    return volume.toFixed(4);
  }

  /**
   * Helper method to split text into lines
   */
  private splitTextIntoLines(text: string, maxCharsPerLine: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    
    if (currentLine) lines.push(currentLine);
    return lines;
  }
}
